// Quick tabs to Accounts & Cheques pages — placed at top of dashboard.
import { useNavigate } from "react-router-dom";
import { Landmark, Receipt } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useAccounts } from "@/lib/useAccounts";
import { useCheques } from "@/lib/useCheques";
import { useCurrencyTotal } from "@/lib/useCurrencyTotal";

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

function Tab({ testid, title, count, total, ccy, Icon, onClick }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="neu p-4 sm:p-5 flex items-center gap-3 w-full active:scale-[0.99] transition-transform text-start">
      <div className="w-11 h-11 rounded-full neu-fill-blue grid place-items-center shadow-md shadow-blue-900/20 shrink-0">
        <Icon className="w-5 h-5 text-white" strokeWidth={2.2} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-display text-lg font-bold neu-accent-blue leading-tight truncate">{title}</p>
        <p className="text-xs text-slate-500 leading-snug">{count}</p>
        <p className="font-display text-base font-extrabold neu-accent-blue tabular-nums leading-snug">
          {fmt(total)} {ccy}
        </p>
      </div>
    </button>
  );
}

export function NeuQuickTabs() {
  const nav = useNavigate();
  const { lang, t } = useI18n();
  const { user } = useAuth();
  const { items: accs = [] } = useAccounts();
  const { items: chqs = [] } = useCheques();
  const home = (user?.currency || "AED").toUpperCase();

  const accEntries = accs.map((a) => ({ amount: Number(a.balance ?? a.opening_balance ?? 0), currency: a.currency }));
  const { total: accTotal } = useCurrencyTotal(accEntries, home);
  const pendingChq = chqs.filter((c) => (c.status || "pending") === "pending");
  const chqEntries = chqs.map((c) => ({ amount: Number(c.amount || 0), currency: c.currency }));
  const { total: chqTotal } = useCurrencyTotal(chqEntries, home);

  const accCountLabel = lang === "ar" ? `${accs.length} ${accs.length === 1 ? "حساب" : "حسابات"}` : `${accs.length} ${accs.length === 1 ? "account" : "accounts"}`;
  const chqCountLabel = lang === "ar"
    ? `${chqs.length} ${chqs.length === 1 ? "شيك" : "شيكات"} · ${pendingChq.length} غير مدفوع`
    : `${chqs.length} total · ${pendingChq.length} unpaid`;

  return (
    <div className="grid grid-cols-1 gap-4">
      <Tab testid="quicktab-accounts" title={t("accounts") || (lang === "ar" ? "حساباتي" : "My accounts")}
        count={accCountLabel} total={accTotal} ccy={home} Icon={Landmark}
        onClick={() => nav("/accounts")} />
      <Tab testid="quicktab-cheques" title={lang === "ar" ? "الشيكات" : "Cheques"}
        count={chqCountLabel} total={chqTotal} ccy={home} Icon={Receipt}
        onClick={() => nav("/cheques")} />
    </div>
  );
}
